import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';

const readingPages = [
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20dynamic%20panel%20layout%20showing%20an%20action%20scene%2C%20detailed%20linework%2C%20speech%20bubbles%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20page%20composition&width=800&height=1100&seq=reading-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20dramatic%20close-up%20of%20characters%20faces%2C%20emotional%20expressions%2C%20detailed%20screen%20tones%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20page%20composition&width=800&height=1100&seq=reading-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20featuring%20an%20intense%20battle%20scene%20with%20speed%20lines%2C%20action%20effects%2C%20dynamic%20angles%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20layout&width=800&height=1100&seq=reading-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20showing%20a%20beautiful%20scenery%20panel%2C%20detailed%20background%20art%2C%20atmospheric%20scene%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20panel%20layout&width=800&height=1100&seq=reading-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20dialogue-heavy%20panels%2C%20character%20interactions%2C%20expressive%20faces%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20page%20composition&width=800&height=1100&seq=reading-page-05&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20climactic%20moment%2C%20full-page%20illustration%2C%20dramatic%20impact%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20page%20composition&width=800&height=1100&seq=reading-page-06&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20character%20transformation%20scene%2C%20energy%20effects%2C%20power-up%20moment%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20layout&width=800&height=1100&seq=reading-page-07&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20showing%20quiet%20conversation%20scene%20between%20two%20characters%2C%20subtle%20expressions%2C%20Japanese%20manga%20art%20style%2C%20professional%20illustration%2C%20clean%20panel%20layout&width=800&height=1100&seq=reading-page-08&orientation=portrait',
];

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const [viewMode, setViewMode] = useState<'info' | 'reading'>('info');
  const [readingChapter, setReadingChapter] = useState<number | null>(null);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-foreground-400 text-3xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的虫虫漫画不存在或已下架</p>
            <Link to="/" className="inline-flex px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    hanman: '韩漫', riman: '日漫', guoman: '国漫', wanjie: '完结',
  };

  const handleChapterClick = (chapterNumber: number) => {
    setReadingChapter(chapterNumber);
    setViewMode('reading');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": comic.title,
        "description": comic.description,
        "author": { "@type": "Person", "name": comic.author },
        "datePublished": comic.chapters[0]?.updatedAt,
        "dateModified": comic.updatedAt,
        "url": `https://chongchongmanga.com/comic/${comic.id}`,
        "inLanguage": "zh-CN"
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://chongchongmanga.com" },
          { "@type": "ListItem", "position": 2, "name": `${categoryMap[comic.categories[0]] || '分类'}专区`, "item": `https://chongchongmanga.com/category/${comic.categories[0]}` },
          { "@type": "ListItem", "position": 3, "name": comic.title, "item": `https://chongchongmanga.com/comic/${comic.id}` }
        ]
      }) }} />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: `${categoryMap[comic.categories[0]] || '分类'}专区`, href: `/category/${comic.categories[0]}` },
          { label: comic.title },
        ]} />

        {viewMode === 'info' ? (
          <>
            {/* Comic Info */}
            <section className="w-full px-4 md:px-6 py-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-64 flex-shrink-0">
                  <div className="relative rounded-lg overflow-hidden bg-background-200 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={`虫虫漫画 - ${comic.title}`}
                      title={`虫虫漫画 - ${comic.title} 免费在线看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <span className="text-sm text-foreground-500 whitespace-nowrap">作者：{comic.author}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === '完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                    }`}>
                      {comic.status}
                    </span>
                    {comic.categories.map(cat => (
                      <Link
                        key={cat}
                        to={`/category/${cat}`}
                        className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {categoryMap[cat] || cat}
                      </Link>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-base w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-lg font-semibold text-foreground-900">{comic.rating}</span>
                    </div>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">
                      <i className="ri-eye-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                      {(comic.views / 10000).toFixed(0)}万次阅读
                    </span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">
                      <i className="ri-file-list-3-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                      {comic.chapters.length}话
                    </span>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => {
                        const lastChapter = comic.chapters[comic.chapters.length - 1];
                        if (lastChapter) handleChapterClick(lastChapter.number);
                      }}
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      开始阅读
                    </button>
                    <button
                      onClick={() => {
                        const firstChapter = comic.chapters[0];
                        if (firstChapter) handleChapterClick(firstChapter.number);
                      }}
                      className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      从第一话开始
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Chapter List */}
            <section className="w-full px-4 md:px-6 py-8 bg-background-100/70">
              <h2 className="text-lg font-heading text-foreground-900 mb-6">章节列表（支持下拉式阅读）</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {[...comic.chapters].reverse().map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => handleChapterClick(ch.number)}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">
                      第{ch.number}话
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.title}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Related */}
            <section className="w-full px-4 md:px-6 py-14">
              <h2 className="text-lg font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <p className="text-sm text-foreground-500">虫虫漫画更多好看的漫画推荐</p>
            </section>
          </>
        ) : (
          <>
            {/* Reading Mode - 下拉式阅读 */}
            <section className="w-full px-4 md:px-6 py-6">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h1 className="text-xl font-heading text-foreground-900">{comic.title}</h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    第{readingChapter}话 · 下拉式阅读
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('info')}
                    className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                    返回详情
                  </button>
                  {readingChapter !== null && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          const prev = comic.chapters.find(c => c.number === readingChapter - 1);
                          if (prev) handleChapterClick(prev.number);
                        }}
                        disabled={readingChapter <= 1}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                      <span className="text-sm text-foreground-500 px-2 whitespace-nowrap">{readingChapter}/{comic.chapters.length}</span>
                      <button
                        onClick={() => {
                          const next = comic.chapters.find(c => c.number === readingChapter + 1);
                          if (next) handleChapterClick(next.number);
                        }}
                        disabled={readingChapter >= comic.chapters.length}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Reading Area - 下拉式漫画内容 */}
              <div className="max-w-2xl mx-auto">
                {readingPages.map((pageUrl, idx) => (
                  <div key={idx} className="mb-4 rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={pageUrl}
                      alt={`虫虫漫画 ${comic.title} 第${readingChapter}话 第${idx + 1}页`}
                      title={`虫虫漫画 - ${comic.title} 下拉式阅读`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                ))}

                {/* Bottom Navigation */}
                <div className="flex items-center justify-between py-8">
                  <button
                    onClick={() => {
                      const prev = comic.chapters.find(c => c.number === readingChapter! - 1);
                      if (prev) handleChapterClick(prev.number);
                    }}
                    disabled={!readingChapter || readingChapter <= 1}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-100 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    上一话
                  </button>
                  <button
                    onClick={() => setViewMode('info')}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-100 text-secondary-700 text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-list-check w-4 h-4 flex items-center justify-center"></i>
                    章节列表
                  </button>
                  <button
                    onClick={() => {
                      const next = comic.chapters.find(c => c.number === readingChapter! + 1);
                      if (next) handleChapterClick(next.number);
                    }}
                    disabled={!readingChapter || readingChapter >= comic.chapters.length}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-100 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    下一话
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </>
  );
}